import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react'
import { User, Session, createClient } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'

const LEGACY_AUTH_STORAGE_KEY = 'auth-storage'

// Se esta aba está em modo de impersonação, usa um client isolado baseado em
// sessionStorage (per-tab) para não interferir na sessão do super admin em outras abas.
function getActiveClient() {
  if (typeof window !== 'undefined' && sessionStorage.getItem('is_impersonating') === 'true') {
    if (!window.__impersonationClient) {
      window.__impersonationClient = createClient(
        import.meta.env.VITE_SUPABASE_URL,
        import.meta.env.VITE_SUPABASE_ANON_KEY,
        {
          auth: {
            autoRefreshToken: true,
            persistSession: true,
            detectSessionInUrl: false,
            storage: window.sessionStorage,
            storageKey: 'sb-impersonation-auth-token',
          },
        }
      )
    }
    return window.__impersonationClient
  }
  return supabase
}

const activeClient = getActiveClient()

interface UserProfile {
  id: string
  full_name: string
  email: string
  role: 'family_member' | 'health_professional' | 'admin'
  auth_user_id: string
}

interface Organization {
  id: string
  name: string
  userRole: 'admin' | 'professional' | 'assistant'
  is_active: boolean
}

interface OrganizationMembershipRow {
  organization_id: string
  role: 'admin' | 'professional' | 'assistant'
  is_active: boolean
  organizations:
    | {
        id: string
        name: string
      }
    | {
        id: string
        name: string
      }[]
    | null
}

function getOrganizationName(
  organizations: OrganizationMembershipRow['organizations'],
) {
  if (!organizations) return 'Organizacao'
  if (Array.isArray(organizations)) {
    return organizations[0]?.name || 'Organizacao'
  }
  return organizations.name
}

interface AuthContextType {
  user: User | null
  session: Session | null
  userProfile: UserProfile | null
  organizations: Organization[]
  currentOrganization: Organization | null
  loading: boolean
  isAdmin: boolean
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string }>
  signOut: () => Promise<void>
  resetPassword: (email: string) => Promise<{ success: boolean; error?: string }>
  updatePassword: (newPassword: string) => Promise<{ success: boolean; error?: string }>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [organizations, setOrganizations] = useState<Organization[]>([])
  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null)
  const [loading, setLoading] = useState(true)
  const isLoadingRef = useRef(false)

  const isAdmin = currentOrganization?.userRole === 'admin'

  useEffect(() => {
    if (typeof window === 'undefined') return

    // Remove stale state left by the retired Zustand auth store.
    localStorage.removeItem(LEGACY_AUTH_STORAGE_KEY)
    sessionStorage.removeItem(LEGACY_AUTH_STORAGE_KEY)
  }, [])

  // Load user profile and organizations
  const loadUserData = useCallback(async (userId: string): Promise<boolean> => {
    // Prevent concurrent calls
    if (isLoadingRef.current) {
      console.log('Already loading user data, skipping...')
      return false
    }

    isLoadingRef.current = true

    try {
      console.log('Loading profile for user:', userId)

      // Load profile
      const { data: profile, error: profileError } = await activeClient
        .from('profiles')
        .select('*')
        .eq('auth_user_id', userId)
        .single()

      console.log('Profile query result:', { profile, error: profileError })

      if (profileError) {
        console.error('Error loading profile:', profileError)
        setUserProfile(null)
        setOrganizations([])
        setCurrentOrganization(null)
        return false
      }

      if (!profile) {
        console.error('No profile found for user')
        setUserProfile(null)
        setOrganizations([])
        setCurrentOrganization(null)
        return false
      }

      setUserProfile(profile)
      console.log('Profile loaded:', profile.full_name)

      // Load organizations
      console.log('Loading organizations for profile:', profile.id)
      const { data: memberships, error: orgError } = await activeClient
        .from('organization_members')
        .select(`
          organization_id,
          role,
          is_active,
          organizations (
            id,
            name
          )
        `)
        .eq('profile_id', profile.id)
        .eq('is_active', true)

      console.log('Organizations query result:', { memberships, error: orgError })

      if (orgError) {
        console.error('Error loading organizations:', orgError)
        setOrganizations([])
        setCurrentOrganization(null)
        return true // Profile loaded but no org
      }

      const orgs: Organization[] = ((memberships ?? []) as OrganizationMembershipRow[]).map((m) => ({
        id: m.organization_id,
        name: getOrganizationName(m.organizations),
        userRole: m.role,
        is_active: m.is_active
      }))

      console.log('Organizations loaded:', orgs.length, orgs)
      setOrganizations(orgs)
      setCurrentOrganization(orgs[0] || null)
      return true
    } catch (error) {
      console.error('Error loading user data:', error)
      return false
    } finally {
      isLoadingRef.current = false
    }
  }, [])

  // Initialize auth
  useEffect(() => {
    let mounted = true

    const initAuth = async () => {
      try {
        console.log('Initializing auth...')
        const { data: { session: currentSession }, error } = await activeClient.auth.getSession()

        if (!mounted) return

        if (error) {
          console.error('Error getting session:', error)
          setLoading(false)
          return
        }

        console.log('Session result:', { hasSession: !!currentSession, userId: currentSession?.user?.id })

        if (currentSession?.user) {
          setSession(currentSession)
          setUser(currentSession.user)
          const success = await loadUserData(currentSession.user.id)
          console.log('User data load result:', success)
        } else {
          console.log('No session found')
        }
      } catch (error) {
        console.error('Error initializing auth:', error)
      } finally {
        if (mounted) {
          console.log('Auth init complete')
          setLoading(false)
        }
      }
    }

    initAuth()

    // Listen for auth changes
    const { data: { subscription } } = activeClient.auth.onAuthStateChange(
      async (event, currentSession) => {
        if (!mounted) return

        console.log('Auth event:', event, { hasSession: !!currentSession })

        switch (event) {
          case 'SIGNED_IN':
            if (currentSession?.user) {
              setSession(currentSession)
              setUser(currentSession.user)
              // Small delay to prevent race with initAuth
              setTimeout(async () => {
                if (mounted && !userProfile) {
                  await loadUserData(currentSession.user.id)
                  setLoading(false)
                }
              }, 100)
            }
            break
          case 'SIGNED_OUT':
            setSession(null)
            setUser(null)
            setUserProfile(null)
            setOrganizations([])
            setCurrentOrganization(null)
            setLoading(false)
            break
          case 'TOKEN_REFRESHED':
            if (currentSession) {
              setSession(currentSession)
              setUser(currentSession.user)
            }
            break
        }
      }
    )

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [loadUserData, userProfile])

  const signIn = useCallback(async (email: string, password: string) => {
    try {
      const { error } = await activeClient.auth.signInWithPassword({ email, password })
      if (error) throw error
      return { success: true }
    } catch (error) {
      return { success: false, error: (error as Error).message }
    }
  }, [])

  const signOut = useCallback(async () => {
    await activeClient.auth.signOut()
  }, [])

  const resetPassword = useCallback(async (email: string) => {
    try {
      const { error } = await activeClient.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/update-password`,
      })
      if (error) throw error
      return { success: true }
    } catch (error) {
      return { success: false, error: (error as Error).message }
    }
  }, [])

  const updatePassword = useCallback(async (newPassword: string) => {
    try {
      const { error } = await activeClient.auth.updateUser({ password: newPassword })
      if (error) throw error
      return { success: true }
    } catch (error) {
      return { success: false, error: (error as Error).message }
    }
  }, [])

  // Debug log
  useEffect(() => {
    console.log('Auth state:', {
      loading,
      hasSession: !!session,
      hasUser: !!user,
      hasProfile: !!userProfile,
      profileName: userProfile?.full_name,
      hasOrg: !!currentOrganization,
      orgName: currentOrganization?.name
    })
  }, [loading, session, user, userProfile, currentOrganization])

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        userProfile,
        organizations,
        currentOrganization,
        loading,
        isAdmin,
        signIn,
        signOut,
        resetPassword,
        updatePassword,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
